import { useCallback, useContext, useEffect, useRef, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useStore } from 'react-redux'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import type { RootState } from '@/store/store'
import { AuthContext } from '@/context/auth-context'
import { useCreateNewAgentV2Chat } from '@/agent-v2/conversations/hooks/useNewAgentV2Chat'
import { AgentV2ConversationsActions } from '@/agent-v2/conversations/store/slice'
import { AgentV2TransportActions } from '@/agent-v2/transport/store/slice'
import { selectAgentV2InitialLoadComplete, selectAgentV2ListError, selectUnsentAgentV2Chat } from '@/agent-v2/conversations/store/selectors'
import { getAgentV2MatterSubmitBlockedReason, kAgentV2MaxTurnInputChars } from '@/agent-v2/utils/agent-v2-contract'
import { useAgentSearchDefaults } from '@/agent-v2/hooks/useAgentSearchDefaults'
import { selectAgentConversationFormHasUnreadyUserFiles, selectAgentConversationFormInputText } from '@/agent-v2/chat-form/store/selectors'
import { AgentConversationFormActions } from '@/agent-v2/chat-form/store/slice'
import { buildConversationPath } from '@/chat-common/conversation-navigation/util/conversation-path-builders'
import { kRouteSegments } from '@/routing/paths'
import { conversationHasAttachedSkills, resolveAttachedSkillDisplayNames, resolveComposerSubmitText } from '@/user-skills/utils/resolve-composer-submit-text'

/**
 * Backs the chat composer on the Matter Overview page.
 *
 * Eagerly creates (or reuses the matter's existing empty) conversation so the composer's
 * file-attach and Skills controls are backed by a real conversation before the first message is
 * sent — matching the composer already shown for an empty chat in AgentV2ChatWindow. Submitting
 * starts the first turn on that conversation and navigates to it, matching how a first message
 * behaves everywhere else in chat.
 */
export function useMatterOverviewChatComposer(matterId: string) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const store = useStore<RootState>()
  const { user } = useContext(AuthContext)
  const currentUserId = user?.uid
  const { createNewAgentV2Chat } = useCreateNewAgentV2Chat()

  const [composerConversationId, setComposerConversationId] = useState<string | null>(null)
  const inputText = useAppSelector((state: RootState) => selectAgentConversationFormInputText(state, { conversationId: composerConversationId ?? '' }))
  const [createError, setCreateError] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const resolvingRef = useRef<Promise<string | null> | null>(null)

  const mattersInitialLoadComplete = useAppSelector((state: RootState) => state.mattersState.initialLoadComplete)
  const agentV2InitialLoadComplete = useAppSelector((state: RootState) => selectAgentV2InitialLoadComplete(state, { matterId }))
  const agentV2ListError = useAppSelector((state: RootState) => selectAgentV2ListError(state, { matterId }))
  const listInitialLoadComplete = mattersInitialLoadComplete && agentV2InitialLoadComplete

  // The sidebar and activity feed load separate data; the composer owns the list it
  // needs to find an existing empty conversation before creating one.
  useEffect(() => {
    dispatch(AgentV2ConversationsActions.refreshConversationList({ matterId, forceRefresh: false }))
  }, [dispatch, matterId])

  const searchDefaults = useAgentSearchDefaults(composerConversationId ?? '')
  const selectedSkillIds = useAppSelector((state) => state.userSkillSelectionState.byConversationId[composerConversationId ?? '']?.selectedSkillIds)
  const selectedPaxtonSkillNames = useAppSelector(
    (state) => state.userSkillSelectionState.byConversationId[composerConversationId ?? '']?.selectedPaxtonSkillNames
  )
  const skillLibrarySkills = useAppSelector((state) => state.userSkillLibraryState.skills)
  const skillLibraryPaxtonSkills = useAppSelector((state) => state.userSkillLibraryState.paxtonSkills)
  const hasUnreadyUserFiles = useAppSelector((state: RootState) =>
    selectAgentConversationFormHasUnreadyUserFiles(state, { conversationId: composerConversationId ?? '' })
  )

  const resolveConversationId = useCallback(async (): Promise<string | null> => {
    if (composerConversationId) return composerConversationId
    if (resolvingRef.current) return resolvingRef.current

    const promise = (async () => {
      const existing = selectUnsentAgentV2Chat(store.getState(), { matterId, currentUserId })
      if (existing) return existing.id
      const response = await createNewAgentV2Chat(false, matterId)
      return response?.id ?? null
    })()
    resolvingRef.current = promise

    const id = await promise
    resolvingRef.current = null
    if (id) {
      setComposerConversationId(id)
      setCreateError(false)
    } else {
      setCreateError(true)
    }
    return id
  }, [composerConversationId, createNewAgentV2Chat, currentUserId, matterId, store])

  // Eagerly resolve once the matter's conversation list has loaded, so file-attach and Skills work
  // before the first message. Never retries after a failed list load — a duplicate empty
  // conversation must not be created just because the list couldn't be confirmed empty (ENG-7069).
  useEffect(() => {
    if (!listInitialLoadComplete || composerConversationId || agentV2ListError) return
    void resolveConversationId()
  }, [agentV2ListError, composerConversationId, listInitialLoadComplete, resolveConversationId])

  const setInputText = useCallback(
    (text: string) => {
      if (!composerConversationId) return
      dispatch(AgentConversationFormActions.setInputText({ conversationId: composerConversationId, text }))
    },
    [composerConversationId, dispatch]
  )

  const retryListLoad = useCallback(() => {
    dispatch(AgentV2ConversationsActions.refreshConversationList({ matterId, forceRefresh: true }))
  }, [dispatch, matterId])

  const matterSubmitBlockedReason = getAgentV2MatterSubmitBlockedReason(matterId)

  const hasAttachedSkills = conversationHasAttachedSkills({ selectedSkillIds, selectedPaxtonSkillNames })
  const attachedSkillDisplayNames = resolveAttachedSkillDisplayNames(
    { selectedSkillIds, selectedPaxtonSkillNames },
    { skills: skillLibrarySkills, paxtonSkills: skillLibraryPaxtonSkills }
  )

  // Expects already-resolved composer text (or null). Callers resolve once via resolveComposerSubmitText.
  const canSubmitResolvedText = useCallback(
    (resolvedText: string | null): resolvedText is string => {
      return resolvedText !== null && resolvedText.length <= kAgentV2MaxTurnInputChars && !matterSubmitBlockedReason && !hasUnreadyUserFiles && !isSubmitting
    },
    [hasUnreadyUserFiles, isSubmitting, matterSubmitBlockedReason]
  )

  const handleSubmit = useCallback(
    async (event?: FormEvent<HTMLFormElement>) => {
      event?.preventDefault()
      const resolvedText = resolveComposerSubmitText(inputText, attachedSkillDisplayNames, hasAttachedSkills)
      if (!canSubmitResolvedText(resolvedText)) return

      setIsSubmitting(true)
      const conversationId = await resolveConversationId()
      if (!conversationId) {
        setIsSubmitting(false)
        return
      }

      dispatch(
        AgentV2TransportActions.startTurnRequested({
          assistantMessageId: crypto.randomUUID(),
          clientRequestId: crypto.randomUUID(),
          conversationId,
          inputText: resolvedText,
          matterId,
          searchDefaults,
          selectedSkillIds,
          selectedPaxtonSkillNames,
          userMessageId: crypto.randomUUID(),
        })
      )
      dispatch(AgentConversationFormActions.setInputText({ conversationId, text: '' }))
      navigate(buildConversationPath(matterId, conversationId, kRouteSegments.agentV2))
    },
    [
      attachedSkillDisplayNames,
      canSubmitResolvedText,
      dispatch,
      hasAttachedSkills,
      inputText,
      matterId,
      navigate,
      resolveConversationId,
      searchDefaults,
      selectedSkillIds,
      selectedPaxtonSkillNames,
    ]
  )

  return {
    composerConversationId,
    // True while there is no real conversation yet to back file-attach/Research/Skills — the list is
    // still loading or a create is in flight. Reused as ChatInputBox's disableActions so it also
    // disables submit until the composer is backed by a real conversation.
    composerNotReady: !composerConversationId,
    hasListError: agentV2ListError,
    hasCreateError: createError,
    hasAttachedSkills,
    hasUnreadyUserFiles,
    inputText,
    matterSubmitBlockedReason,
    onInputTextChange: setInputText,
    onSubmit: handleSubmit,
    retryListLoad,
    retryCreate: resolveConversationId,
  }
}
